import { signal } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { AccessToken } from './Models/access-token.interface';
import { IdToken } from './Models/id-token.interface';
import { ensureJwtDecode } from './util/ensure-jwt-decode.fn';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { DatadogService } from 'src/app/shared/services/datadog.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private oauthService = inject(OAuthService);
  private router = inject(Router);
  private datadogService = inject(DatadogService);

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  // Key used to store the return URL in storage during the login process.
  private static readonly RETURN_URL_KEY = 'auth_return_url';

  // Various states of the user.
  public readonly idToken = signal<Record<string, unknown> | null>(null);
  public readonly accessToken = signal<AccessToken | null>(null);
  public readonly userDetails = signal<IdToken | null>(null);
  public readonly isAuthenticated = signal<boolean>(false);

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([this.isAuthenticated$, this.isDoneLoading$]).pipe(
    map(values => values.every(b => b))
  );

  private navigateToLoginPage() {
    this.router.navigateByUrl('/');
  }

  constructor() {
    this.oauthService.events.subscribe(() => {
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
    });
    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

    this.oauthService.events.pipe(filter(e => ['token_received'].includes(e.type))).subscribe(async () => {
      const userGuid = await this.userGuid;
      const roles = await this.roles;
      await this.datadogService.enrichWithUser(userGuid, roles);
    });
    this.oauthService.events.pipe(filter(e => ['discovery_document_loaded'].includes(e.type))).subscribe(async () => {
      const userGuid = await this.userGuid;
      const roles = await this.roles;
      await this.datadogService.enrichWithUser(userGuid, roles);
    });

    this.oauthService.events
      .pipe(filter(e => ['session_terminated', 'session_error'].includes(e.type)))
      .subscribe(() => this.navigateToLoginPage());

    this.oauthService.setupAutomaticSilentRefresh();
  }

  public async runInitialLoginSequence(config: AuthConfig): Promise<void> {
    this.clearTokensOnAuthConfigChange();
    this.oauthService.setStorage(localStorage);
    this.oauthService.configure(config);

    try {
      this.saveReturnUrl();
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();
      await this.setStates();
      this.oauthService.setupAutomaticSilentRefresh();
    } catch (error) {
      if (this.isStaleStateError(error)) {
        console.warn('Stale OAuth state detected, clearing state and retrying login...');
        this.clearOAuthState();
        try {
          await this.oauthService.loadDiscoveryDocumentAndTryLogin();
          await this.setStates();
          this.oauthService.setupAutomaticSilentRefresh();
        } catch (retryError) {
          console.error('Error during discovery document or login after retry:', retryError);
        }
      } else {
        console.error('Error during discovery document or login:', error);
      }
    } finally {
      this.isDoneLoadingSubject$.next(true);
    }

    await this.navigateToReturnUrl();
  }

  private saveReturnUrl(): void {
    const params = new URLSearchParams(window.location.search);
    if (params.has('code')) return; // OAuth callback — original URL was already saved
    const url = window.location.pathname + window.location.search;
    localStorage.setItem(AuthService.RETURN_URL_KEY, url);
  }

  private async navigateToReturnUrl(): Promise<void> {
    const returnUrl = localStorage.getItem(AuthService.RETURN_URL_KEY);
    if (!returnUrl) return;
    await this.router.navigateByUrl(returnUrl);
  }

  private clearOAuthState() {
    const keysToRemove = [
      'nonce',
      'PKCE_verifier',
      'session_state',
      'access_token',
      'id_token',
      'refresh_token',
      'expires_at',
      'id_token_claims_obj',
    ];
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  }

  private async setStates() {
    this.idToken.set(this.getIdToken());
    this.userDetails.set(this.getUserDetails());
    this.isAuthenticated.set(this.oauthService.hasValidAccessToken());
    try {
      const token = await this.getDecodedAccessToken();
      this.accessToken.set(token);
    } catch {
      this.accessToken.set(null);
    }
  }

  private isStaleStateError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return message.includes('wrong state') || message.includes('wrong nonce');
  }

  private clearTokensOnAuthConfigChange(): void {
    const storageKey = 'oauth_config_fingerprint';
    const currentFingerprint = `${this.oauthService.clientId}|${this.oauthService.issuer}`;
    const storedFingerprint = localStorage.getItem(storageKey);

    if (storedFingerprint && storedFingerprint !== currentFingerprint) {
      this.oauthService.logOut(true);
    }

    localStorage.setItem(storageKey, currentFingerprint);
  }

  public login(targetUrl?: string) {
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }

  public logout() {
    this.oauthService.logOut();
  }
  public refresh() {
    this.oauthService.silentRefresh();
  }
  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  get roles() {
    const permissions = this.accessTokenPermissions;
    if (permissions && permissions.length > 0) {
      return permissions;
    }
    const identityClaims = this.identityClaims;
    if (!identityClaims) {
      return null;
    }
    return identityClaims['realm_access']?.roles;
  }
  public hasRole(allowedRoles: string): boolean {
    if (!allowedRoles) {
      return false;
    }
    const rolesArray = allowedRoles.split(',');
    if (this.roles) {
      return this.roles.some(r => rolesArray.includes(r));
    }
    return false;
  }

  public get userGuid() {
    return this.getUserSub();
  }

  private async getUserSub(): Promise<string | null> {
    const identityClaims = await this.identityClaims;
    if (!identityClaims) {
      return null;
    }
    type ObjectKey = keyof typeof identityClaims;
    const subVar = 'sub' as ObjectKey;
    const sub = identityClaims[subVar];
    return sub;
  }

  public async getDecodedAccessToken(): Promise<AccessToken | null> {
    const accessToken = this.oauthService.getAccessToken();
    if (!accessToken) {
      return null;
    }

    const fullToken = this.oauthService.getAccessToken();
    await ensureJwtDecode();
    const jwt = await import('jwt-decode');

    const jwtModule = jwt as unknown;
    let decoder: (p: string) => unknown;
    if (typeof jwtModule === 'function') {
      decoder = jwtModule as (p: string) => unknown;
    } else {
      const obj = jwtModule as Record<string, unknown>;
      if (typeof obj['jwtDecode'] === 'function') {
        decoder = obj['jwtDecode'] as (p: string) => unknown;
      } else {
        decoder = obj['default'] as (p: string) => unknown;
      }
    }

    const token = decoder(fullToken) as unknown as AccessToken;

    const result: AccessToken = {
      ...token,
      issuer: token.issuer ?? '',
      permissions: token.permissions ?? [],
      connection_id: token['https://ok.dk/connection_id'] ?? '',
      azure_ad_groups: token['https://ok.dk/azure_ad_groups'] ?? [],
      realm_access: token.realm_access ?? { roles: [] },
    };

    return result;
  }

  public get refreshToken() {
    return this.oauthService.getRefreshToken();
  }
  public get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }

  public getIdToken() {
    return this.oauthService.getIdentityClaims();
  }

  public get logoutUrl() {
    return this.oauthService.logoutUrl;
  }
  public get preferredUsername() {
    return this.getPreferredUsername();
  }

  private async getPreferredUsername(): Promise<string | null> {
    const identityClaims = await this.identityClaims;
    if (!identityClaims) {
      return null;
    }

    type ObjectKey = keyof typeof identityClaims;
    const preferredUsername = identityClaims['preferred_username' as ObjectKey];
    const email = identityClaims['email' as ObjectKey];

    return preferredUsername || email || null;
  }

  public getUserDetails(): IdToken | null {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }

    return {
      aud: claims['aud'] ?? '',
      email: claims['email'] ?? '',
      email_verified: claims['email_verified'] ?? false,
      exp: claims['exp'] ?? NaN,
      family_name: claims['family_name'] ?? '',
      given_name: claims['given_name'] ?? '',
      iat: claims['iat'] ?? NaN,
      iss: claims['iss'] ?? '',
      nickname: claims['nickname'] ?? '',
      issuer: claims['issuer'] ?? '',
      picture: claims['picture'] ?? '',
      name: claims['name'] ?? '',
    };
  }

  private get accessTokenPermissions(): string[] | null {
    const token = this.oauthService.getAccessToken();
    if (!token) {
      return null;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload['permissions'] ?? [];
    } catch {
      return null;
    }
  }
}
